/**
 * King Living override — default `default` keeps the GetBlogRelatedArticlesById query under
 * Contentful complexity limits; carousel cards still get tile image + category via
 * RelatedBlogCardForArticlesFragment. Use `full` only when you need the legacy deep Blog shape.
 */

import {print} from 'graphql';

import {useQuery} from '@tanstack/react-query';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {
  getBaseBlogRelatedArticlesById,
  getBlogRelatedArticlesById,
  getFullBlogRelatedArticlesById,
} from '@amblique/contentful/app/contentful/queries';

/**
 * @param {string} id - Contentful entry id
 * @param {Object} [options={}]
 * @param {'base'|'default'|'full'} [options.variant='default'] - Default matches slim carousel fragment
 */
export function useBlogRelatedArticlesById(id, options = {}) {
  const contentfulService = useService(ContentfulService);

  const {
    variant = 'default',
    variables = {},
    queryOptions = {},
    requestOptions = {},
  } = options;

  const query = variant === 'base' ?
    getBaseBlogRelatedArticlesById :
    variant === 'full' ?
      getFullBlogRelatedArticlesById :
      getBlogRelatedArticlesById;

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'blogRelatedArticles',
      'id',
      id,
      variant,
      contentfulService?.locale,
      JSON.stringify(variables),
    ],

    queryFn: async () => {
      if (!contentfulService) {
        throw new Error('ContentfulService not available');
      }

      const data = await contentfulService.query(
        print(query),
        {
          id,
          locale: contentfulService?.locale,
          preview: false,
          ...variables,
        },
        requestOptions
      );

      return data.blogRelatedArticles || null;
    },

    enabled: !!contentfulService && !!id,

    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,

    ...queryOptions,
  });
}
